'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/components/ui/use-toast';
import Mannequin3D from './Mannequin3D';

interface AvatarCreatorProps {
  onAvatarCreated?: (taskId: string) => void;
  onCancel?: () => void;
}

interface CreateAvatarForm {
  name: string;
  prompt: string;
  negative_prompt?: string;
}

export default function AvatarCreator({ onAvatarCreated, onCancel }: AvatarCreatorProps) {
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<CreateAvatarForm>();

  const onSubmit = async (data: CreateAvatarForm) => {
    setIsCreating(true);
    
    try {
      const response = await apiClient.createAvatar({
        prompt: data.prompt,
        negative_prompt: data.negative_prompt || "ugly, deformed, disfigured, poor quality, low quality"
      });

      if (response.task_id) {
        toast({
          title: "Avatar Creation Started",
          description: "Your avatar is being generated. This may take a few minutes.",
        });
        
        onAvatarCreated?.(response.task_id);
        reset();
      } else {
        throw new Error("Failed to start avatar creation");
      }
    } catch (error) {
      console.error('Error creating avatar:', error);
      toast({
        title: "Creation Failed",
        description: error instanceof Error ? error.message : "Failed to create avatar",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Preview Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Preview</h3>
        <Card>
          <CardContent className="p-4">
            <Mannequin3D 
              className="w-full h-80"
              showCreateButton={false}
            />
            <p className="text-sm text-gray-600 mt-4 text-center">
              This is an example of what your avatar could look like. Describe your ideal avatar in the form to get started!
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Creation Form */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Create Your Avatar</h3>
        <Card>
          <CardHeader>
            <CardTitle>Avatar Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Avatar Name</Label>
                <Input
                  id="name"
                  placeholder="My Awesome Avatar"
                  {...register('name', { 
                    required: 'Avatar name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' }
                  })}
                />
                {errors.name && (
                  <p className="text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="prompt">Description</Label>
                <Textarea
                  id="prompt"
                  placeholder="Describe your avatar... (e.g., 'A professional-looking person with short brown hair, wearing a blue shirt, friendly smile')"
                  rows={4}
                  {...register('prompt', { 
                    required: 'Avatar description is required',
                    minLength: { value: 10, message: 'Description must be at least 10 characters' }
                  })}
                />
                {errors.prompt && (
                  <p className="text-sm text-red-600">{errors.prompt.message}</p>
                )}
                <p className="text-sm text-gray-500">
                  Be specific about appearance, clothing, and style for best results.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="negative_prompt">What to Avoid (Optional)</Label>
                <Textarea
                  id="negative_prompt"
                  placeholder="Things you don't want in your avatar... (e.g., 'glasses, hat, beard')"
                  rows={2}
                  {...register('negative_prompt')}
                />
                <p className="text-sm text-gray-500">
                  Specify things you want to avoid in your avatar.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="submit" 
                  disabled={isCreating}
                  className="flex-1"
                >
                  {isCreating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    'Create Avatar'
                  )}
                </Button>
                {onCancel && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={onCancel}
                    disabled={isCreating}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tips for Better Avatars</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Be specific about physical features (hair color, eye color, etc.)</li>
              <li>Mention clothing style and colors</li>
              <li>Include facial expressions (smile, serious, etc.)</li>
              <li>Specify the setting or background if desired</li>
              <li>Use clear, descriptive language</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}